import { Text, Tooltip } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconAlertTriangle, IconExclamationMark } from '@tabler/icons-react'
import { Translate } from 'react-admin'
import { PermissionApiDTO, UserApiDTO } from '../codegen/generated/Api.schemas'

export const UsersHelper = {
  ADMIN_ROLE: 'ADMIN',
  ROLES_PERMISSIONS: [
    {
      group: 'youtubeChannels',
      items: ['youtubeChannels.read', 'youtubeChannels.edit'],
    },
    {
      group: 'beneficiaries',
      items: ['beneficiaries.read', 'beneficiaries.edit', 'beneficiary_rates.edit'],
    },
    {
      group: 'products',
      items: ['products.read', 'products.edit'],
    },
    {
      group: 'artworks',
      items: ['artworks.read', 'artworks.edit'],
    },
    {
      group: 'youtubeClaimsAssets',
      items: ['youtubeClaimsAssets.read', 'youtubeClaimsAssets.edit'],
    },
    {
      group: 'compilations',
      items: ['compilations.read', 'compilations.edit'],
    },
    { group: 'reports', items: ['imports.edit'] },
    { group: 'statements', items: ['statements.read', 'statements.edit'] },
    {
      group: 'automatedTreatments',
      items: [
        'auto_monetization.read',
        'monetization_patterns.edit',
        'auto_translation.read',
        'auto_translation.edit',
      ],
    },
    {
      group: 'videoPromotions',
      items: ['video_promotion.read', 'video_promotion.edit'],
    },
    {
      group: 'fileCopy',
      items: ['file_copy.read', 'file_copy.edit'],
    },
    {
      group: 'exports',
      items: ['scam_export.read'],
    },
    {
      group: 'quotas',
      items: ['youtubeQuotas.read'],
    },
    { group: 'users', items: ['users.edit', 'user_roles.read', 'user_rights.edit'] },
    { group: 'roles', items: ['roles.read', 'roles.edit'] },
    {
      group: 'others',
      items: [
        'media_uploader.access',
        'media_uploader.cold_storage_access',
        'content_id.edit',
        'dataviz.read',
      ],
    },
  ] as Array<{ group: string; items: PermissionApiDTO[] }>,
  CMS: [
    { value: 'logicomix', label: 'Logicomix' },
    { value: 'bump', label: 'Bump' },
    { value: 'endemol', label: 'BMF' },
    { value: 'tv5monde', label: 'TV5 Monde' },
    { value: 'france_tv_medias', label: 'France TV Médias' },
  ],
  permissionsSync: (permissions: PermissionApiDTO[]) => {
    /* Add read permission if edit permissions clicked */
    for (const category of [
      'beneficiaries',
      'products',
      'artworks',
      'youtubeChannels',
      'youtubeClaimsAssets',
      'compilations',
      'statements',
      'roles',
      'auto_translation',
      'video_promotion',
      'file_copy',
    ]) {
      if (
        permissions.includes(`${category}.edit` as PermissionApiDTO) &&
        !permissions.includes(`${category}.read` as PermissionApiDTO)
      ) {
        permissions.push(`${category}.read` as PermissionApiDTO)
      }
    }
    if (permissions.includes('user_rights.edit') && !permissions.includes('user_roles.read')) {
      /* Specific case for user rights */
      permissions.push('user_roles.read')
    }

    return permissions
  },
  getPermissionsList: (_permissions: PermissionApiDTO[], t: Translate): JSX.Element => {
    if (_permissions[0] === '*') {
      return (
        <Text fs="italic" size="sm">
          {t('_generics.all_female')}
        </Text>
      )
    }

    return (
      <Text tt="lowercase" size="sm">
        {_permissions.length} {t('pages.users.permissions')}
      </Text>
    )
  },
  getDeactivatedUser: (user: UserApiDTO | undefined, label: string, size = 16) => {
    if (user?.roles?.length === 0)
      return (
        <Tooltip label={label} w={280} multiline>
          <IconAlertTriangle size={size} color="orange" />
        </Tooltip>
      )
  },
  showUnauthorizedNotification: (permission: PermissionApiDTO, t: Translate) => {
    notifications.show({
      color: 'orange',
      icon: <IconExclamationMark />,
      title: t('pages.users.lackingPermission'),
      message: `→ ${t(`pages.users.permissionsList.${permission}`)}`,
      withCloseButton: true,
      autoClose: 3600,
    })
  },
}
